'use client';

import React, { useState } from 'react';
import LoginForm from './components/LoginForm';
import SignupForm from './components/SignupForm';
import AppLogo from '@/components/ui/AppLogo';
import { ShieldCheck, Lock, Zap, TrendingUp } from 'lucide-react';

export default function AuthPage() {
  const [mode, setMode] = useState<'login' | 'signup'>('login');

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-[55%] blob-navy flex-col justify-between p-10 xl:p-14 relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-white/5 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-accent/10 translate-y-1/3 -translate-x-1/3" />

        {/* Logo */}
        <div className="flex items-center gap-3 relative z-10">
          <AppLogo size={40} />
          <span className="text-xl font-bold text-white tracking-tight">LedgerPay</span>
        </div>

        {/* Hero Copy */}
        <div className="relative z-10">
          <h1 className="text-4xl xl:text-5xl font-extrabold text-white leading-tight mb-4">
            Intelligent Bill Pay.<br />
            <span className="text-accent">Human-Authorized.</span>
          </h1>
          <p className="text-base text-white/60 leading-relaxed max-w-md mb-8">
            LedgerPay maps live invoice data against real-time banking balances to build an optimized payment queue — with every dollar behind a cryptographic authorization gate.
          </p>

          {/* Trust signals */}
          <div className="space-y-3">
            {[
              { icon: ShieldCheck, text: 'Human-In-The-Loop authorization — no automated payments ever' },
              { icon: Lock, text: 'Cryptographic token signing with full audit trail per transaction' },
              { icon: Zap, text: 'AI-scored priority queue updates in real time as invoices arrive' },
              { icon: TrendingUp, text: 'CFO liquidity forecasting with 30-day cash runway projection' },
            ]?.map((item, i) => (
              <div key={`trust-${i}`} className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                  <item.icon size={14} className="text-accent" />
                </div>
                <p className="text-sm text-white/70 leading-snug">{item?.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10">
          <p className="text-xs text-white/30">SOC 2 Type II · PCI DSS Level 1 · Bank-grade 256-bit encryption</p>
        </div>
      </div>
      {/* Right Form Panel */}
      <div className="flex-1 flex flex-col justify-center items-center px-6 py-10 bg-background">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <AppLogo size={32} />
            <span className="font-bold text-lg text-foreground">LedgerPay</span>
          </div>

          {/* Mode Toggle */}
          <div className="flex bg-muted p-1 rounded-xl mb-8">
            <button
              onClick={() => setMode('login')}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${mode === 'login' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Sign In
            </button>
            <button
              onClick={() => setMode('signup')}
              className={`flex-1 py-2 text-sm font-semibold rounded-lg transition-all duration-200 ${mode === 'signup' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Create Account
            </button>
          </div>

          {mode === 'login' ? <LoginForm /> : <SignupForm onSwitchToLogin={() => setMode('login')} />}
        </div>
      </div>
    </div>
  );
}